import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@core/auth/AuthProvider';
import AuthGate from '@core/auth/AuthGate';
import AppLayout from '@core/components/AppLayout';
import { PeriodProvider } from '@core/components/PeriodProvider';
import { ToastProvider } from '@core/components/Toast';
import RequirePermission from '@core/components/RequirePermission';
import { MastersProvider } from '@core/masters/MastersProvider';
import MastersPage from '@core/masters/MastersPage';
import ExpenseApplyPage from '@core/flow/ExpenseApplyPage';
import MyApplicationsPage from '@core/flow/MyApplicationsPage';
import ApprovalFlowPage from '@core/flow/ApprovalFlowPage';
import ReceiveMoneyPage from '@core/flow/ReceiveMoneyPage';
import CashPage from '@core/cash/CashPage';
import DepartmentPlPage from '@core/pl/DepartmentPlPage';
import CompanyPlPage from '@core/pl/CompanyPlPage';
import AccountPlPage from '@core/pl/AccountPlPage';
import UnsoPlPage from '@modules/unso/UnsoPlPage';
import RoutineClosingsPage from '@core/routine/RoutineClosingsPage';
import RoutineClosingPrintPage from '@core/routine/RoutineClosingPrintPage';
import BalancesPage from '@core/pl/BalancesPage';
import PayQueuePage from '@core/queue/PayQueuePage';
import InvoicesPage from '@core/receivables/InvoicesPage';
import InvoicePrintPage from '@core/receivables/InvoicePrintPage';
import RevenueEntryPage from '@core/transactions/RevenueEntryPage';
import BudgetPage from '@core/budget/BudgetPage';
import RecurringCostsPage from '@core/recurring/RecurringCostsPage';
import TransactionListPage from '@core/transactions/TransactionListPage';
import CashMatrixPage from '@core/cash/CashMatrixPage';
import LedgerPage from '@core/ledger/LedgerPage';
import ReceiptPrintPage from '@core/flow/ReceiptPrintPage';
import ReportPage from '@core/flow/ReportPage';
import ReportConfirmPage from '@core/flow/ReportConfirmPage';
import NewsPage from '@core/flow/NewsPage';
import TodoPage from '@core/flow/TodoPage';
import { APP, appBaseUrl, boardAccessOf, boardHomePath, seesUnso, shouldRenderHere } from '@core/apps/appTarget';
import OtherAppNotice from '@core/apps/OtherAppNotice';

/**
 * Gate と Board を別のアプリに(2026-10-23)。いまのアプリの画面ならそのまま出し、相手のアプリの画面なら案内を出す。
 * 各画面の入室条件(下の RequirePermission)は変えていない
 */
function AppBoundary() {
  const { pathname } = useLocation();
  return shouldRenderHere(APP, pathname, appBaseUrl('board', import.meta.env)) ? <Outlet /> : <OtherAppNotice />;
}

/** Board を使えない人(どの Board の画面も見られない人)への案内 */
function NoBoardAccess() {
  const gate = appBaseUrl('gate', import.meta.env);
  return (
    <section className="screen">
      <div className="card">
        <h3>Nexus Board で見られる画面がありません</h3>
        <p className="hint" style={{ margin: '0 0 12px' }}>
          申請・承認などは Nexus Gate で行います。必要な場合は管理者(本部)に権限の付与を依頼してください。
        </p>
        {gate && (
          <a className="btn-green" style={{ display: 'inline-block', textDecoration: 'none' }} href={gate} target="_blank" rel="noopener noreferrer">
            Nexus Gate を開く
          </a>
        )}
      </div>
    </section>
  );
}

function AppRoutes() {
  const { user, can, canConfirm, flowRole } = useAuth();
  const isAdmin = can('master_admin');
  const seesHonbu =
    can('company_pl') ||
    canConfirm ||
    (can('department_pl') && (user?.department_ids.includes('honbu') ?? false));
  // Board で最初に開く画面(各画面の入室条件と同じ。src/core/apps/appTarget.ts の boardMenu)
  const boardAccess = boardAccessOf({ can, canConfirm, flowRole, user });
  const boardHome = boardHomePath(boardAccess);
  const home = APP === 'board' ? (boardHome ?? '/no-access') : '/apply';

  return (
    <Routes>
      {/* 受領書の印刷ページ(サイドバーなしの単独レイアウト)。Gate の画面 */}
      {APP === 'gate' && <Route path="/receipt/:id" element={<ReceiptPrintPage />} />}
      {/* 請求書の印刷ページ(2026-10-23 段階3)。読めるのは経理・管理者・承認者(権限ルール)。Gate の画面 */}
      {APP === 'gate' && <Route path="/invoice/:id" element={<InvoicePrintPage />} />}
      {/* Routine の締めの確定の印刷ページ(2026-10-23)。読めるのは経理・承認者・管理者(権限ルール)。Gate の画面 */}
      {APP === 'gate' && <Route path="/routine/print/:id" element={<RoutineClosingPrintPage />} />}
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to={home} replace />} />
        {APP === 'board' && <Route path="/no-access" element={<NoBoardAccess />} />}
        {/* Board で印刷ページの URL を開いたときは Gate への案内 */}
        {APP === 'board' && <Route path="/receipt/:id" element={<OtherAppNotice />} />}
        {APP === 'board' && <Route path="/invoice/:id" element={<OtherAppNotice />} />}
        {APP === 'board' && <Route path="/routine/print/:id" element={<OtherAppNotice />} />}
        <Route element={<AppBoundary />}>
        {/* やること(■17 第1弾・2026-08-31): 担当ステップへのショートカット。全権限が使える */}
        <Route path="/todo" element={<TodoPage />} />
        <Route
          path="/transactions"
          element={
            <RequirePermission allowed={flowRole === 'accounting'}>
              <TransactionListPage />
            </RequirePermission>
          }
        />
        <Route path="/apply" element={<ExpenseApplyPage />} />
        {/* 経費申請(承認不要)=事後報告(2026-08-26 会議)。全権限が使用可 */}
        <Route path="/report" element={<ReportPage />} />
        <Route path="/mylist" element={<MyApplicationsPage />} />
        {/* お知らせ欄(通知センター・2026-08-26 会議) */}
        <Route path="/news" element={<NewsPage />} />
        <Route
          path="/revenue"
          element={
            <RequirePermission allowed={can('transaction_input')}>
              <RevenueEntryPage />
            </RequirePermission>
          }
        />
        {/* ステータス別のフロー作業ページ(2026-08-26 分割): 承認/支払い/受領/完了確認 */}
        <Route
          path="/approve"
          element={
            <RequirePermission allowed={flowRole === 'bucho' || flowRole === 'approver'}>
              <ApprovalFlowPage page="approve" />
            </RequirePermission>
          }
        />
        {/* 支払い・受領は全員(「自分の支払い」「自分の受領」があるため) */}
        <Route path="/pay" element={<ApprovalFlowPage page="pay" />} />
        {/* 請求書・入金(2026-10-23 段階3)。作る・記録は経理・管理者、見るのは承認者も */}
        <Route
          path="/invoices"
          element={
            <RequirePermission allowed={flowRole === 'accounting' || flowRole === 'approver' || isAdmin}>
              <InvoicesPage />
            </RequirePermission>
          }
        />
        {/* Routine の締め(2026-10-23・見るだけ)。Routine の確定の写しと Gate での状態。経理・承認者・管理者(支払キューと同じ) */}
        <Route
          path="/routine"
          element={
            <RequirePermission allowed={flowRole === 'accounting' || flowRole === 'approver' || isAdmin}>
              <RoutineClosingsPage />
            </RequirePermission>
          }
        />
        {/* 支払キュー(2026-10-23・見るだけの一覧)。経理・承認者・管理者 */}
        <Route
          path="/queue"
          element={
            <RequirePermission allowed={flowRole === 'accounting' || flowRole === 'approver' || isAdmin}>
              <PayQueuePage />
            </RequirePermission>
          }
        />
        {/* 受領=受け取ったお金 / 受領待ち=まだ受け取っていないお金(2026-10-15 作り直し) */}
        <Route path="/receive" element={<ReceiveMoneyPage mode="received" />} />
        <Route path="/receive-wait" element={<ReceiveMoneyPage mode="waiting" />} />
        {/* 経理確認(支払い済み申請・2026-08-27) */}
        <Route
          path="/acct-confirm"
          element={
            <RequirePermission allowed={flowRole === 'accounting' || isAdmin}>
              <ReportConfirmPage />
            </RequirePermission>
          }
        />
        <Route
          path="/confirm"
          element={
            <RequirePermission allowed={flowRole === 'approver'}>
              <ApprovalFlowPage page="confirm" />
            </RequirePermission>
          }
        />
        <Route
          path="/cf"
          element={
            <RequirePermission allowed={canConfirm}>
              <CashPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ledger"
          element={
            <RequirePermission allowed={canConfirm || isAdmin}>
              <LedgerPage />
            </RequirePermission>
          }
        />
        <Route
          path="/cf/matrix"
          element={
            <RequirePermission allowed={canConfirm}>
              <CashMatrixPage />
            </RequirePermission>
          }
        />
        <Route
          path="/recurring"
          element={
            <RequirePermission allowed={canConfirm || isAdmin}>
              <RecurringCostsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/budget"
          element={
            <RequirePermission allowed={can('company_pl') && isAdmin}>
              <BudgetPage />
            </RequirePermission>
          }
        />
        <Route
          path="/pl/dept"
          element={
            <RequirePermission allowed={flowRole === 'bucho' && (user?.manager_department_ids.length ?? 0) > 0}>
              <DepartmentPlPage departmentId={user?.manager_department_ids[0]} />
            </RequirePermission>
          }
        />
        {/* 運送PL(2026-10-23)。入れる人は appTarget.ts の seesUnso(全社PL・経理/承認者・運送の部門長) */}
        <Route
          path="/pl/unso"
          element={
            <RequirePermission allowed={seesUnso(boardAccess)}>
              <UnsoPlPage />
            </RequirePermission>
          }
        />
        <Route
          path="/pl/honbu"
          element={
            <RequirePermission allowed={seesHonbu}>
              <DepartmentPlPage />
            </RequirePermission>
          }
        />
        <Route
          path="/pl"
          element={
            <RequirePermission allowed={can('company_pl')}>
              <CompanyPlPage />
            </RequirePermission>
          }
        />
        {/* 勘定科目別の予実(2026-10-23)。入れる人は全社PLと同じ */}
        <Route
          path="/pl/accounts"
          element={
            <RequirePermission allowed={can('company_pl')}>
              <AccountPlPage />
            </RequirePermission>
          }
        />
        {/* 売掛・買掛・未払残高(B4・2026-10-23)。「確定」の権限の人(経理・管理者・承認者=入出金の記録を読める人)。Board のメニューに出す */}
        <Route
          path="/pl/balances"
          element={
            <RequirePermission allowed={canConfirm}>
              <BalancesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/master"
          element={
            <RequirePermission allowed={isAdmin}>
              <MastersPage />
            </RequirePermission>
          }
        />
        </Route>
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <PeriodProvider>
          <BrowserRouter>
            <AuthGate>
              <MastersProvider>
                <AppRoutes />
              </MastersProvider>
            </AuthGate>
          </BrowserRouter>
        </PeriodProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
